/**
 * @render react
 * @name Navigation
 */

import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { compose } from 'recompose';

import { withPolaris } from 'Components/protons/Polaris';

import { Button } from 'Components/atoms/Button';
import { NavigationItem } from 'Components/molecules/NavigationItem';

import './_style.scss';

/**
 * Navigation component
 *
 * @param {string} action - Action for sub menus to be displayed. Either hover or click
 * @param {boolean} activeWildcard - Checks whether we want exact or wildcard match for the active menu item
 * @param {string} ampOnButtonClick - amp string for amp-bind component
 * @param {string} ariaLabel - aria-label attribute
 * @param {string} buttonChildElement - Child element. Can be span or svg. Default set to none.
 * @param {string} buttonClassName - Class name for the mobile button
 * @param {string} buttonLabel - Label for the mobile button
 * @param {array} content - Array containing objects with the following keys: url, title, target, extra classes
 * @param {Object} context - Extra information coming from Polaris provider.
 * @param {Object} extraClassNames - Extra class names to be applied to the component
 * @param {string} id - Unique identifier for this navigation.
 * @param {boolean} loadStyles - Whether to apply the class names for styling
 * @param {string} navigationClassName - navigation class name
 * @param {function} onButtonClick - when the mobile hamburger is clicked
 * @param {boolean} responsive - Whether we want the navigation to be responsive.
 * @param {string} subMenuClickBreakpoint - Breakpoint for sub menus with children to be clickable
 * @param {string} subMenuIcon - Icon for sub menus
 * @param {string} viewAllLabel - The label when a menu item is promoted
 *
 */
const Navigation = ({
  action,
  activeWildcard,
  ampOnButtonClick,
  ariaLabel,
  buttonAriaLabel,
  buttonChildElement,
  buttonClassName,
  buttonLabel,
  content,
  context: { cssPrefix },
  extraClassNames,
  extraContent,
  extraSubMenuContent,
  id,
  loadStyles,
  navigationClassName,
  onButtonClick,
  responsive,
  responsiveNavHeading,
  showSubMenuTitle,
  subMenuClickBreakpoint,
  subMenuIcon,
  viewAllLabel,
}) => {
  const ButtonChildElementTag = ['span', 'svg'].indexOf(buttonChildElement) >= 0 ? buttonChildElement : '';
  // Ref to track the parent's hover state
  const parentRef = useRef([]);

  const [hoveredParent, setHoveredParent] = useState(null);
  // Get the breakpoint from CSS
  const breakpoint = typeof window !== 'undefined'
    ? window
      .getComputedStyle(document.querySelector('body'), ':before')
      .getPropertyValue('content')
      .replace(/"/g, '')
    : 'desktop'; // Default value for SSR/testing
  const isMobileOrTablet = breakpoint === 'mobile' || breakpoint === 'tablet';

  const handleMouseEnter = (index, isMobileOrTabletArg) => {
    if (isMobileOrTabletArg) return; // Prevent mouse enter on mobile/tablet
    setHoveredParent(index);
  };

  const handleMouseLeave = (isMobileOrTabletArg) => {
    if (isMobileOrTabletArg) return; // Prevent mouse leave on mobile/tablet
    setHoveredParent(null);
  };
  useEffect(
    () => (
      () => {
        parentRef.current.forEach((parent) => {
          if (parent) {
            parent.removeEventListener('mouseenter', () => {});
            parent.removeEventListener('mouseleave', () => {});
          }
        });
        parentRef.current = [];
      }
    ),
    [],
  );

  return (
    <div
      className={classNames({
        [`${cssPrefix}__navigation`]: loadStyles,
        ...extraClassNames,
      })}
    >
      {responsive && (
        <div className={`${cssPrefix}__navigation--heading-wrapper`}>
          {responsiveNavHeading && (
            <p className={`${cssPrefix}__heading ${cssPrefix}__navigation--heading -no-spacing`}>{responsiveNavHeading}</p>
          )}
          <Button
            ampOn={ampOnButtonClick}
            extraClassNames={{
              [`${buttonClassName}`]: true,
            }}
            label={buttonLabel}
            onClick={onButtonClick}
            srLabel={buttonAriaLabel}
            type="button"
            variant=""
          >
            {ButtonChildElementTag && <ButtonChildElementTag />}
          </Button>
        </div>
      )}

      <div className={navigationClassName}>
        <nav aria-label={ariaLabel} id={id || undefined}>
          <ul>
            {content.map((item, index) => (
              <NavigationItem
                action={action}
                activeWildcard={activeWildcard}
                item={item}
                key={item?.id || index}
                onClickCallback={onButtonClick}
                showSubMenuTitle={showSubMenuTitle}
                subMenuClickBreakpoint={subMenuClickBreakpoint}
                extraSubMenuContent={
                    hoveredParent === index ? extraSubMenuContent : null
                  }
                subMenuIcon={subMenuIcon}
                viewAllLabel={viewAllLabel}
                isMobileOrTablet={isMobileOrTablet}
                breakpoint={breakpoint}
                parentRefCallback={(element) => {
                  if (element) {
                    parentRef.current[index] = element;
                    element.addEventListener('mouseenter', () => handleMouseEnter(index, isMobileOrTablet));
                    element.addEventListener('mouseleave', () => handleMouseLeave(isMobileOrTablet));
                  }
                }}
              />
            ))}
          </ul>
        </nav>
      </div>
      {extraContent}
    </div>
  );
};

/**
 * Valid prop types
 */
Navigation.propTypes = {
  action: PropTypes.string,
  activeWildcard: PropTypes.bool,
  ampOnButtonClick: PropTypes.string,
  ariaLabel: PropTypes.string,
  buttonAriaLabel: PropTypes.string,
  buttonChildElement: PropTypes.oneOf(['svg', 'span', '']),
  buttonClassName: PropTypes.string,
  buttonLabel: PropTypes.string,
  content: PropTypes.arrayOf(PropTypes.shape()),
  context: PropTypes.shape(),
  extraClassNames: PropTypes.instanceOf(Object),
  extraSubMenuContent: PropTypes.element,
  extraContent: PropTypes.element,
  id: PropTypes.string,
  loadStyles: PropTypes.bool,
  navigationClassName: PropTypes.string,
  onButtonClick: PropTypes.func,
  responsive: PropTypes.bool,
  responsiveNavHeading: PropTypes.string,
  showSubMenuTitle: PropTypes.bool,
  subMenuClickBreakpoint: PropTypes.string,
  subMenuIcon: PropTypes.string,
  viewAllLabel: PropTypes.string,
};

/**
 * Default props
 */
Navigation.defaultProps = {
  action: 'hover',
  activeWildcard: true,
  ampOnButtonClick: '',
  ariaLabel: null,
  buttonAriaLabel: 'Menu',
  buttonChildElement: '',
  buttonClassName: '-button',
  buttonLabel: 'Menu',
  content: [],
  context: {},
  extraClassNames: {},
  extraContent: null,
  extraSubMenuContent: null,
  id: null,
  loadStyles: true,
  navigationClassName: '-navigation',
  onButtonClick: null,
  responsive: false,
  responsiveNavHeading: '',
  showSubMenuTitle: false,
  subMenuClickBreakpoint: 'mobile',
  subMenuIcon: null,
  viewAllLabel: 'View all',
};

export default compose(withPolaris)(Navigation);
